/**
 * WorkspaceMembersPanel
 * =====================
 *
 * Per-workspace member grants, embedded in the workspace detail page
 * (workspace management) — moved here from Settings, which now manages
 * ORGANIZATION members instead.
 *
 * A workspace can only be shared with people who are already members
 * of the workspace's organization (i.e. they accepted their org
 * invitation). The add-member picker therefore lists org members, not
 * a free-form email field; inviting somebody new happens in
 * Settings -> Organization Members.
 *
 * Backend (via the ``workspaces`` redux-saga namespace):
 *   GET    /workspace/<id>/members
 *   POST   /workspace/<id>/members        { user_id, role }
 *   DELETE /workspace/<id>/members/<uid>
 *   GET    /organization/mine             (resolve org + manage rights)
 *   GET    /organization/<org>/members    (the eligible-people list)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Button, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  CircularProgress, Tooltip, Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupIcon from '@mui/icons-material/Group';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';

import {
  fetchRequest as fetchMembers,
  clear as clearMembers,
} from '../../redux/actions/workspaces/members';
import {
  fetchRequest as fetchEligible,
  clear as clearEligible,
} from '../../redux/actions/workspaces/eligibleMembers';
import {
  fetchRequest as addRequest,
  clear as clearAdd,
} from '../../redux/actions/workspaces/memberAdd';
import { fetchRequest as removeRequest } from '../../redux/actions/workspaces/memberRemove';

const ROLES = ['viewer', 'editor', 'admin'];
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const WorkspaceMembersPanel = ({ workspaceId, organizationId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const membersSlice = useSelector((s) => s.workspace_members) || {};
  const eligibleSlice = useSelector((s) => s.workspace_eligible_members) || {};
  const addSlice = useSelector((s) => s.workspace_member_add) || {};

  const [initialized, setInitialized] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [role, setRole] = useState('editor');

  useEffect(() => {
    dispatch(clearMembers());
    dispatch(clearEligible());
    dispatch(clearAdd());
    setInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (workspaceId) dispatch(fetchMembers({ workspaceId }));
  }, [workspaceId, dispatch]);

  // Resolve the org (prop wins; else the viewer's first Team+ org) and
  // load its member roster for the picker — one saga flow. A 403 just
  // means the viewer can't manage org members — the add form stays hidden.
  useEffect(() => {
    dispatch(fetchEligible({ organizationId }));
  }, [organizationId, dispatch]);

  // Spinner until the first load lands (mutation reloads keep the table).
  const loading = !initialized || (!membersSlice.response && !membersSlice.error);
  const members = useMemo(() => {
    if (membersSlice.error) return [];
    const m = membersSlice.response?.members;
    return Array.isArray(m) ? m : [];
  }, [membersSlice.response, membersSlice.error]);

  // null = not loaded / no rights
  const orgMembers = eligibleSlice.response ? eligibleSlice.response.members : null;
  const adding = Boolean(addSlice.isLoading);

  const memberIds = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);
  const eligible = useMemo(
    () => (orgMembers || []).filter((m) => !memberIds.has(m.user_id)),
    [orgMembers, memberIds],
  );

  // Member added (snackbar comes from the saga) — reset the picker.
  useEffect(() => {
    if (addSlice.response) setSelectedUser('');
  }, [addSlice.response]);

  const handleAdd = () => {
    if (!selectedUser) { enqueueSnackbar('Pick an organization member', { variant: 'warning' }); return; }
    dispatch(addRequest({ workspaceId, userId: selectedUser, role }));
  };

  const handleRemove = (uid) => {
    dispatch(removeRequest({ workspaceId, userId: uid }));
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <GroupIcon fontSize="small" color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Members</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Who can access this workspace. Only organization members can be
        added — invite new people from{' '}
        <RouterLink to="/settings/members">Organization Members</RouterLink>{' '}
        first.
      </Typography>

      {orgMembers !== null && (
        eligible.length > 0 ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }} alignItems="flex-start">
            <TextField
              select size="small" label="Organization member"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              sx={{ minWidth: 280 }}
            >
              {eligible.map((m) => (
                <MenuItem key={m.user_id} value={m.user_id}>
                  {m.name ? `${m.name} — ${m.email}` : m.email}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" label="Role" value={role}
              onChange={(e) => setRole(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              {ROLES.map((r) => <MenuItem key={r} value={r}>{cap(r)}</MenuItem>)}
            </TextField>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={adding || !selectedUser}
              sx={{ mt: { xs: 0, sm: 0.25 } }}
            >
              {adding ? 'Adding…' : 'Add member'}
            </Button>
          </Stack>
        ) : (
          <Alert severity="info" sx={{ mb: 2.5 }}>
            Every organization member already has access (or there's nobody
            else to add). Invite more people from{' '}
            <RouterLink to="/settings/members">Organization Members</RouterLink>.
          </Alert>
        )
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    No members yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {members.map((m) => (
              <TableRow key={m.user_id} hover>
                <TableCell>{m.name || '—'}</TableCell>
                <TableCell>{m.email || m.user_id}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={m.role}
                    color={m.is_owner ? 'primary' : 'default'}
                    variant={m.is_owner ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell align="right">
                  {m.is_owner ? (
                    <Tooltip title="The owner can't be removed">
                      <span>
                        <IconButton size="small" disabled>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <IconButton size="small" onClick={() => handleRemove(m.user_id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      )}
    </Paper>
  );
};

export default WorkspaceMembersPanel;
